import { useCallback } from "react";
import { objectToFormData } from "../../../../../utils/objectToFormData";
import requestCache from "../../../../../utils/requestCache";
import { useAuth } from "../../../../auth/hooks/useAuth";
import { IWizardConfig, WizardMode } from "../../../../wizard/types";
import useUserQueries from "../../../hooks/useUserQueries";
import { User } from "../../../types";
import { parseToAccountBackEndModel } from "../helpers/parseToAccountBackEndModel";
import { parseToAccountFrontEndModel } from "../helpers/parseToAccountFrontEndModel";
import { EditProfileForm } from "./EditProfileForm";
import { formValidator } from "./validators";
import { UserRole } from "../../../enums/UserRole";
import { queryCache } from "react-query";
import { QUERY_KEYS } from "../../../../../constants";

export function useUpdateProfileConfig(passedUser?: User): IWizardConfig {
  const { user: authUser, profile } = useAuth();

  const { fetchUpdateProfile, fetchUpdateMyProfile, fetchUserProfile } =
    useUserQueries();

  const { adminUsersList } = QUERY_KEYS;

  const targetUser = passedUser ? passedUser : authUser;

  const userIsAuthUser = !passedUser || passedUser.id === authUser.id;

  const updateAccount = useCallback(
    async (data) => {
      const accountData = objectToFormData(
        parseToAccountBackEndModel(data, targetUser.role),
      );
      const isAdminOrSupport = [
        UserRole.Owner,
        UserRole.Admin,
        UserRole.Support,
      ].includes(authUser?.role);

      if (isAdminOrSupport) {
        await queryCache.invalidateQueries([adminUsersList]);
      }

      return await (userIsAuthUser
        ? fetchUpdateMyProfile(accountData)
        : fetchUpdateProfile(targetUser?.id, accountData));
    },
    [userIsAuthUser, targetUser, authUser],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const getParsedProfile = useCallback<User | any>(async () => {
    let fetchedProfile: any;

    if (passedUser) {
      fetchedProfile = await requestCache<any>(
        `profile_for_edit_${passedUser.id}`,
        () => fetchUserProfile(passedUser.id),
        { forceFetchOnMount: true },
      );
    }

    if (!passedUser && (!profile || !profile.data || !profile.data.data)) {
      return;
    }

    const targetProfile = passedUser ? fetchedProfile.data : profile.data.data;

    return parseToAccountFrontEndModel(
      targetUser,
      targetProfile,
      targetUser.role,
    );
  }, [targetUser, passedUser, profile]);

  return {
    steps: [
      {
        width: "568px",
        titleConfig: {
          titleText: "Edit Profile",
        },
        content: EditProfileForm,
        validator: formValidator(targetUser.role),
      },
    ],
    submitButtonText: "Save",
    mode: WizardMode.Edit,
    saveCallback: updateAccount,
    getInitialData: getParsedProfile,
  };
}
